import { useState, useEffect, useCallback } from 'react';
import { Bot, X, Save, Loader2, Clipboard, Check, AlertTriangle, ArrowLeft, Sparkles } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import api from '../../utils/api';

// Konstanten
const UNITS = ['Meter', 'Stueck', 'Rolle', 'kg', 'Liter', 'Paket', 'Set'];
const CATEGORIES = [
  'Kabel',
  'Leitungen',
  'Schalter',
  'Steckdosen',
  'Verteiler',
  'Beleuchtung',
  'Werkzeug',
  'Befestigung',
  'Rohre',
  'Sonstiges',
];

const SUPPLIERS = [
  { code: 'obeta', name: 'Obeta', color: 'bg-blue-500' },
  { code: 'sonepar', name: 'Sonepar', color: 'bg-red-500' },
  { code: 'unielektro', name: 'UniElektro', color: 'bg-green-500' },
  { code: 'zajadacz', name: 'Zajadacz', color: 'bg-orange-500' },
];

/**
 * Product Batch Modal - Paste multiple screenshots, auto-detect suppliers
 */
const ProductBatchModal = ({ isOpen, onClose, onProductSaved }) => {
  // Image collection state
  const [images, setImages] = useState([]);

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload' | 'result'

  // Analysis result
  const [analysisResult, setAnalysisResult] = useState(null);

  // Product data (editable after analysis)
  const [productData, setProductData] = useState({
    name: '',
    category: '',
    unit: '',
    quantity: '',
    search_keywords: '',
  });
  const [supplierData, setSupplierData] = useState({});

  // Reset state on close
  const handleClose = () => {
    setImages([]);
    setAnalysisResult(null);
    setProductData({
      name: '',
      category: '',
      unit: '',
      quantity: '',
      search_keywords: '',
    });
    setSupplierData({});
    setStep('upload');
    setError(null);
    onClose();
  };

  // Handle file from paste or drop
  const handleImageAdd = useCallback((file) => {
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Nur Bildformate (PNG, JPG, WEBP, GIF) erlaubt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Bild zu gross (max. 10MB)');
      return;
    }

    if (images.length >= 6) {
      setError('Maximal 6 Bilder erlaubt');
      return;
    }

    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImages(prev => [...prev, {
        id: Date.now() + Math.random(),
        file,
        preview: e.target.result,
      }]);
    };
    reader.readAsDataURL(file);
  }, [images.length]);

  // Paste event listener - captures paste events when this modal is open
  useEffect(() => {
    if (!isOpen || step !== 'upload') return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          // WICHTIG: Stoppe Event-Propagation damit andere Listener nicht auch reagieren
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const file = item.getAsFile();
          handleImageAdd(file);
          return;
        }
      }
    };

    // Mit capture: true um Events vor anderen Listenern zu fangen
    window.addEventListener('paste', handlePaste, true);
    return () => window.removeEventListener('paste', handlePaste, true);
  }, [isOpen, step, handleImageAdd]);

  // Remove image
  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Analyze images
  const analyzeImages = async () => {
    if (images.length === 0) {
      setError('Mindestens ein Bild erforderlich');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      images.forEach((img, index) => {
        formData.append('images', img.file);
      });

      const response = await api.post('/products/analyze-auto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success && response.data.result) {
        const result = response.data.result;
        setAnalysisResult(result);

        // Set product data
        setProductData({
          name: result.product?.name || '',
          category: result.product?.category || '',
          unit: result.product?.unit || '',
          quantity: result.product?.quantity || '',
          search_keywords: result.product?.search_keywords || '',
        });

        // Set supplier data
        const newSupplierData = {};
        SUPPLIERS.forEach(s => {
          const found = result.suppliers?.find(rs => rs.code === s.code);
          newSupplierData[s.code] = {
            detected: found?.detected || false,
            confidence: found?.confidence || 0,
            sku: found?.sku || '',
            price: found?.price || '',
            priceBase: found?.priceBase || null,
            copperSurcharge: found?.copperSurcharge || null,
            priceIncludesCopper: found?.priceIncludesCopper || null,
            packageSize: found?.packageSize || '',
            pricePerUnit: found?.pricePerUnit || null,
            imageIndex: found?.imageIndex,
          };
        });
        setSupplierData(newSupplierData);

        setStep('result');
      } else {
        setError(response.data.error || 'K.I. konnte die Bilder nicht analysieren');
      }
    } catch (err) {
      console.error('Auto-detect analyze error:', err);
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Save product
  const saveProduct = async () => {
    if (!productData.name?.trim()) {
      setError('Produktname erforderlich');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Find best price
      const prices = SUPPLIERS
        .map(s => parseFloat(supplierData[s.code]?.price))
        .filter(p => !isNaN(p) && p > 0);
      const bestPrice = prices.length > 0 ? Math.min(...prices) : null;

      // Find supplier with best price
      const bestSupplier = SUPPLIERS.find(s => {
        const price = parseFloat(supplierData[s.code]?.price);
        return price === bestPrice;
      });

      // Build supplier_links
      const supplierLinks = SUPPLIERS
        .filter(s => supplierData[s.code]?.detected || supplierData[s.code]?.sku || supplierData[s.code]?.price)
        .map(s => ({ name: s.name, url: '' }));

      // Create product
      const createData = {
        name: productData.name,
        category: productData.category,
        unit: productData.unit,
        quantity: productData.quantity ? parseInt(productData.quantity) : null,
        price: bestPrice,
        supplier: bestSupplier?.name || null,
        supplier_links: supplierLinks,
        search_keywords: productData.search_keywords
          .split(',')
          .map(k => k.trim())
          .filter(Boolean),
      };

      const productResponse = await api.post('/products', createData);
      const newProductId = productResponse.data?.id;

      if (newProductId) {
        // Add supplier sources - speichere ALLE erkannten Lieferanten
        for (const supplier of SUPPLIERS) {
          const data = supplierData[supplier.code];

          // Speichern wenn: detected=true ODER sku vorhanden ODER price vorhanden
          // (vorher fehlte detected, daher wurden manche Lieferanten nicht gespeichert)
          if (data?.detected || data?.sku || data?.price) {
            try {
              await api.post(`/products/${newProductId}/sources`, {
                supplier: supplier.name,
                price: data.price ? parseFloat(data.price) : null,
                article_number: data.sku || null,
                package_size: data.packageSize ? parseInt(data.packageSize, 10) : null,
                price_per_unit: data.pricePerUnit || null,
              });
            } catch (err) {
              console.warn(`Source for ${supplier.name} failed:`, err.message);
            }
          }
        }
      }

      onProductSaved?.(productResponse.data);
      handleClose();
    } catch (err) {
      console.error('Save product error:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // Update supplier field
  const updateSupplierField = (code, field, value) => {
    setSupplierData(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value,
      },
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-500" />
          <span>Produkt mit K.I. anlegen</span>
        </div>
      }
      size="xl"
    >
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Paste Images */}
        {step === 'upload' && (
          <>
            {/* Instructions */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                Screenshots einfuegen
              </h3>
              <p className="text-gray-500 text-sm">
                Machen Sie Screenshots von Produktseiten verschiedener Lieferanten.
                Die K.I. erkennt automatisch Obeta, Sonepar, UniElektro und Zajadacz.
              </p>
            </div>

            {/* Paste Area */}
            <div
              className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-colors
                ${images.length === 0
                  ? 'border-primary-300 bg-primary-50/50'
                  : 'border-gray-300 bg-gray-50'}
              `}
            >
              <Clipboard className="w-12 h-12 text-primary-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                <kbd className="px-2 py-1 bg-white border rounded shadow-sm text-primary-600 font-mono">Ctrl+V</kbd>
                {' '}druecken um Screenshot einzufuegen
              </p>
              <p className="text-sm text-gray-500">
                Die K.I. erkennt automatisch den Lieferanten anhand von Logo, URL und Design
              </p>
            </div>

            {/* Image Previews */}
            {images.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Eingefuegte Screenshots ({images.length})
                  </h4>
                  <span className="text-xs text-gray-400">
                    Max. 6 Bilder
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img, index) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.preview}
                        alt={`Screenshot ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                        #{index + 1}
                      </div>
                      <button
                        onClick={() => removeImage(img.id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {images.length < 6 && (
                    <div className="h-24 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                      + Ctrl+V
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={handleClose}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                onClick={analyzeImages}
                disabled={images.length === 0 || isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    K.I. analysiert {images.length} {images.length === 1 ? 'Bild' : 'Bilder'}...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 mr-2" />
                    Analysieren ({images.length})
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Step 2: Results */}
        {step === 'result' && (
          <>
            {/* Product Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <Check className="w-5 h-5" />
                <span className="font-medium">Erkannte Daten (korrigieren falls noetig)</span>
              </div>

              <Input
                label="Produktname *"
                value={productData.name}
                onChange={(e) => setProductData({ ...productData, name: e.target.value })}
                placeholder="z.B. NYM-J 3x1,5 mm² Tr 500m"
              />

              <div className="grid grid-cols-3 gap-4">
                <Select
                  label="Kategorie"
                  value={productData.category}
                  onChange={(e) => setProductData({ ...productData, category: e.target.value })}
                  options={[
                    { value: '', label: 'Waehlen...' },
                    ...CATEGORIES.map(c => ({ value: c, label: c })),
                  ]}
                />
                <Select
                  label="Einheit"
                  value={productData.unit}
                  onChange={(e) => setProductData({ ...productData, unit: e.target.value })}
                  options={[
                    { value: '', label: 'Waehlen...' },
                    ...UNITS.map(u => ({ value: u, label: u })),
                  ]}
                />
                <Input
                  label="Menge pro VE"
                  type="number"
                  value={productData.quantity}
                  onChange={(e) => setProductData({ ...productData, quantity: e.target.value })}
                  placeholder="z.B. 500"
                />
              </div>

              <Input
                label="Suchbegriffe (komma-getrennt)"
                value={productData.search_keywords}
                onChange={(e) => setProductData({ ...productData, search_keywords: e.target.value })}
                placeholder="NYM, Mantelleitung, 3x1.5, ..."
              />
            </div>

            {/* Supplier Data */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Erkannte Lieferanten</h3>
              <div className="border rounded-lg overflow-hidden">
                {SUPPLIERS.map((supplier, index) => {
                  const data = supplierData[supplier.code] || {};
                  const isDetected = data.detected;

                  return (
                    <div
                      key={supplier.code}
                      className={`
                        flex items-center gap-4 p-3
                        ${index > 0 ? 'border-t' : ''}
                        ${isDetected ? 'bg-green-50' : 'bg-gray-50'}
                      `}
                    >
                      {/* Status Icon */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                        ${isDetected ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'}
                      `}>
                        {isDetected ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </div>

                      {/* Supplier Name */}
                      <div className="w-28 flex-shrink-0">
                        <span className="font-medium text-gray-800">{supplier.name}</span>
                        {isDetected && data.confidence > 0 && (
                          <span className="block text-xs text-green-600">
                            {Math.round(data.confidence * 100)}% sicher
                          </span>
                        )}
                      </div>

                      {/* Input Fields */}
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Art.Nr."
                            value={data.sku || ''}
                            onChange={(e) => updateSupplierField(supplier.code, 'sku', e.target.value)}
                            className="text-sm"
                          />
                          <div className="relative">
                            <Input
                              placeholder="Gesamtpreis"
                              type="number"
                              step="0.01"
                              value={data.price || ''}
                              onChange={(e) => updateSupplierField(supplier.code, 'price', e.target.value)}
                              className="text-sm pr-8"
                              title="Gesamtpreis inkl. Kupferzuschlag"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Input
                              placeholder="VE"
                              type="number"
                              value={data.packageSize || ''}
                              onChange={(e) => {
                                const pkgSize = e.target.value;
                                const price = parseFloat(data.price);
                                const pricePerUnit = (price && pkgSize && parseFloat(pkgSize) > 0)
                                  ? Math.round((price / parseFloat(pkgSize)) * 100) / 100
                                  : null;
                                updateSupplierField(supplier.code, 'packageSize', pkgSize);
                                updateSupplierField(supplier.code, 'pricePerUnit', pricePerUnit);
                              }}
                              className="text-sm w-20"
                              title="Preisbezug (z.B. 100 bei 'Preis pro 100m')"
                            />
                            {data.pricePerUnit && (
                              <span className="text-xs text-green-600 font-medium whitespace-nowrap" title="Preis pro Einheit">
                                = {data.pricePerUnit.toFixed(2)}€/E
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Kupferzuschlag Info */}
                        {(data.copperSurcharge || data.priceIncludesCopper) && (
                          <div className="text-xs text-orange-600 pl-1 flex items-center gap-2">
                            {data.copperSurcharge && (
                              <span title="Kupferzuschlag separat ausgewiesen">
                                (inkl. {data.copperSurcharge.toFixed(2)}€ Kupfer)
                              </span>
                            )}
                            {!data.copperSurcharge && data.priceIncludesCopper && (
                              <span title="Preis enthält Kupferzuschlag">
                                (inkl. Zuschläge)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('upload');
                  setAnalysisResult(null);
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Zurueck
              </Button>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  onClick={saveProduct}
                  disabled={!productData.name?.trim() || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Produkt speichern
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProductBatchModal;
